import { ExternalLinkIcon } from "@heroicons/react/outline";
import React from "react";
import { Link } from "react-router-dom";

import { ContextMenu } from "../../ContextMenu";
import { ContextMenuItems } from "../../ContextMenu/types";
import { Action, DropdownMenuVariant } from "../../DropdownMenu";

import { CardBody } from "./CardBody";
import { CardHeader } from "./CardHeader";
import { CardMetadata } from "./CardMetadata";
import { CardTags } from "./CardTags";
import { CardVariant } from "./constants";
import { CardProps } from "./types";
import { convertActionsToContextMenuItems } from "./utils";

export const Card = <DataT,>({
  onClick,
  link,
  className = "",
  contentClassName = "",
  title,
  icon,
  subtitle,
  metadata = [],
  tags = [],
  maxVisibleTags = 3,
  body,
  actions,
  item,
  tagsSeparator,
  useBadge,
  emptyMetadataMessage,
  emptyTagsMessage,
  variant,
  shouldShowEmptyText,
  loadingState,
  disabled = false,
  modal = false,
  useLink = true,
  headerActions,
}: CardProps<DataT>) => {
  const isSelected = variant === CardVariant.SELECTED;
  const isDisabledState = variant === CardVariant.DISABLED || disabled;

  const getBaseClasses = () => {
    if (isDisabledState) {
      return "flex flex-col rounded-lg border border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed w-full min-h-42 h-full";
    }
    if (isSelected) {
      return "flex flex-col rounded-lg border border-blue-400 bg-blue-50 transition-all duration-200 ease-in-out cursor-pointer w-full min-h-42 h-full";
    }
    return "flex flex-col rounded-lg border border-gray-200 bg-white transition-all duration-200 ease-in-out hover:border-gray-300 hover:shadow-sm cursor-pointer w-full min-h-42 h-full";
  };

  const baseClasses = getBaseClasses();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    if (isDisabledState) {
      e.preventDefault();
      return;
    }
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const hasActionableItems = Boolean(actions && actions.length > 0);
  const additionalItems = hasActionableItems
    ? [{ id: "divider", isDivider: true }, ...(actions || [])]
    : [];

  const dropdownMenuItems: Action<DataT>[] = [
    {
      id: "open-in-new-tab",
      label: "Open in new tab",
      icon: ExternalLinkIcon,
      onClick: () => window.open(link, "_blank"),
      variant: DropdownMenuVariant.PRIMARY,
    },
    ...additionalItems,
  ];

  // Check if we're showing batch actions by looking for batch-specific action IDs
  // Batch actions include "batch-move" and "batch-delete" but not "Open in new tab"
  const isBatchAction = actions?.some(
    action =>
      typeof action === "object" &&
      "id" in action &&
      (action.id === "batch-move" || action.id === "batch-delete"),
  );

  const contextMenuActions = isBatchAction
    ? ((actions || []) as Action<DataT>[])
    : dropdownMenuItems;

  const defaultContextMenuItems: ContextMenuItems<DataT>[] = convertActionsToContextMenuItems(
    contextMenuActions,
  ) as ContextMenuItems<DataT>[];

  const hasMetadata = metadata.length > 0;
  const hasBody = Boolean(body);
  const hasTags = tags.length > 0;

  const cardContent = (
    <div
      className={`mt-0.5 flex h-full w-full flex-col px-3 pb-2 pt-1.5 text-gray-900 transition-colors duration-200 ease-in-out ${contentClassName}`}
    >
      <div className="flex flex-shrink-0 items-start">
        <div className="min-w-0 flex-1">
          <CardHeader
            variant={variant}
            icon={icon}
            loadingState={loadingState}
            title={title}
            item={item}
            subtitle={subtitle}
            singleLine={!body}
            actions={actions}
            disabled={disabled}
            dropdownMenuItems={dropdownMenuItems}
            headerActions={headerActions}
          />
        </div>
      </div>
      <div
        className={`flex min-h-0 flex-1 flex-col items-start justify-between ${
          hasMetadata || hasBody || hasTags ? "flex" : "hidden"
        }`}
      >
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          <CardMetadata emptyText={emptyMetadataMessage} metadata={metadata} />
          <CardBody body={body} />
        </div>
        <div className="w-full flex-shrink-0">
          <CardTags
            emptyText={emptyTagsMessage}
            tags={tags}
            shouldShowEmptyText={shouldShowEmptyText}
            maxVisibleTags={maxVisibleTags}
            separator={tagsSeparator}
            useBadge={useBadge}
          />
        </div>
      </div>
    </div>
  );

  return (
    <ContextMenu
      modal={modal}
      contextMenuItems={isDisabledState ? [] : defaultContextMenuItems}
      data={item}
      isDisabled={isDisabledState}
    >
      {useLink ? (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          to={isDisabledState ? "#" : link}
          className={`${baseClasses} ${className} group`}
          onClick={handleClick}
        >
          {cardContent}
        </Link>
      ) : (
        <div className={`${baseClasses} ${className} group`} onClick={handleClick}>
          {cardContent}
        </div>
      )}
    </ContextMenu>
  );
};

export type { MetadataItem } from "./CardMetadata";
export { CardsGrid } from "./CardsGrid";
export { CardTagsSeparator } from "./CardTags";
export { CardVariant } from "./constants";
